import { useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { GrantSpec } from "@equipped/core/backend/client";

/**
 * "＋ Add user" (docs/286-ADMIN-ADD-USER-WITHOUT-INVITE.md v1) as an MUI `Dialog` — mirrors
 * `apps/admin-web/src/components/AddUserModal.tsx`: pre-provisions a real, granted account with
 * **no invite code**; the account is credential-less until the person's first verified-identity
 * sign-in claims it.
 *
 * **v0 simplification** (matching `apps/admin-web`'s own v0, kept for this evaluation build's
 * People-tab scope): grants here are entered as plain `kind:ref` rows instead of the richer
 * catalog-tree `GrantPicker` (used on the Invites tab) — `adminCreateUser` itself is unchanged
 * (`GrantSpec[]` either way).
 */
export function AddUserDialog({
  open,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (input: { email: string; firstName?: string; lastName?: string; grants: GrantSpec[] }) => void;
}) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [grants, setGrants] = useState<GrantSpec[]>([]);
  const [grantKind, setGrantKind] = useState<GrantSpec["kind"]>("pack");
  const [grantRef, setGrantRef] = useState("");

  const reset = () => {
    setEmail("");
    setFirstName("");
    setLastName("");
    setGrants([]);
    setGrantRef("");
  };

  const close = () => {
    reset();
    onClose();
  };

  const addGrantRow = () => {
    const ref = grantRef.trim();
    if (!ref) return;
    if (grants.some((g) => g.kind === grantKind && g.ref === ref)) return;
    setGrants((list) => [...list, { kind: grantKind, ref }]);
    setGrantRef("");
  };

  const removeGrantRow = (kind: GrantSpec["kind"], ref: string) =>
    setGrants((list) => list.filter((g) => !(g.kind === kind && g.ref === ref)));

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Add a person</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Creates the account immediately with the grants below — no invite code involved. It's
          credential-less until this person signs in with a matching identity (Sign in with Apple)
          for the first time.
        </Typography>
        <Stack spacing={2}>
          <TextField
            type="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="First name (optional)"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={busy}
              fullWidth
            />
            <TextField
              label="Last name (optional)"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={busy}
              fullWidth
            />
          </Stack>

          <Typography variant="subtitle2" sx={{ mb: -1 }}>
            Grants (optional)
          </Typography>
          {grants.length > 0 ? (
            <List dense sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              {grants.map((g) => (
                <ListItem
                  key={`${g.kind}:${g.ref}`}
                  secondaryAction={
                    <IconButton edge="end" size="small" onClick={() => removeGrantRow(g.kind, g.ref)} aria-label="Remove grant">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText primary={`${g.kind}: ${g.ref}`} />
                </ListItem>
              ))}
            </List>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
            <TextField
              select
              size="small"
              value={grantKind}
              onChange={(e) => setGrantKind(e.target.value as GrantSpec["kind"])}
              disabled={busy}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="pack">pack</MenuItem>
              <MenuItem value="catalog">catalog</MenuItem>
            </TextField>
            <TextField
              size="small"
              placeholder="ref (e.g. phase-1)"
              value={grantRef}
              onChange={(e) => setGrantRef(e.target.value)}
              disabled={busy}
              fullWidth
            />
            <Button variant="outlined" color="secondary" onClick={addGrantRow} disabled={busy || !grantRef.trim()}>
              Add
            </Button>
          </Stack>
        </Stack>
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={busy || !email.trim()}
          onClick={() =>
            onSubmit({
              email: email.trim(),
              firstName: firstName.trim() || undefined,
              lastName: lastName.trim() || undefined,
              grants,
            })
          }
        >
          Add user
        </Button>
      </DialogActions>
    </Dialog>
  );
}
